/* eslint-disable max-lines */
import { skipToken } from '@reduxjs/toolkit/query';
import { type ReferentPriceType, RouteCodes } from '@strata/constants';
import { DateFormats, formatDate, formatNameLastFirst } from '@strata/helper-functions';
import { CheckboxListFormik } from '@strata/react-checkbox-list';
import { ReasonInput } from '@strata/react-compound-inputs';
import { MultiStepDialog } from '@strata/react-dialog';
import { Hyperlink } from '@strata/react-hyperlink';
import { InlineMessage } from '@strata/react-inline-message';
import { DetailLabelValuePair } from '@strata/react-label-value-pairs';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { formatRateString, ServicingRate } from '@strata/react-servicing-rate';
import { SuccessToast } from '@strata/react-toast-message';
import { type WorklistTask } from '@strata/react-worklist';
import { validationStrings } from '@strata/strata-ui-strings';
import dayjs from 'dayjs';
import { useFormikContext } from 'formik';
import { type ReactNode, useEffect, useMemo, useRef } from 'react';
import * as Yup from 'yup';

import { getConvertBiosimilarErrorMessage } from 'services/biosimilarConversionHelpers';
import { handleError } from 'services/Error';
import { AppRoutes } from 'services/routes';
import { useAppDispatch } from 'store';
import {
    type BiosimilarConversionProduct,
    useGetBiosimilarConversionProductsQuery,
    useGetConfiguredProductsForPayerQuery,
} from 'store/api/referenceApi';
import {
    type ConversionCleanlinessPreview,
    type ReferralDetails,
    useConvertBiosimilarMutation,
    useRunConversionCleanlinessPreviewMutation,
} from 'store/api/treatmentApi';
import { usePostConvertMutation } from 'store/api/worklistApi';
import { fetchWorklistTasks } from 'store/worklist/worklistThunks';

const Title = 'Convert to Biosimilar';

const ReasonOptions = [
    'Coverage exception',
    'Member preference',
    'Prescriber preference',
    'Pharmacy preference',
];

const AcceptRateOptions = {
    AGREE_TO_RATE: 'agreeToRate',
    AGREE_TO_TERMS: 'agreeToTerms',
} as const;

type ConvertBiosimilarFormValues = {
    destinationProduct?: BiosimilarConversionProduct;
    acceptRate?: string[];
} & typeof ReasonInput.initialValues;

export interface ConvertBiosimilarMemberDetails {
    memberId?: string;
    firstName?: string;
    lastName?: string;
    memberState?: string;
    memberZip?: string;
    benefitGroup?: string;
    lineOfBusiness?: string;
    lobSegment?: string;
    dateOfBirth?: string;
}

interface OfferRate {
    rate?: string | number;
    referentPriceType?: ReferentPriceType;
}

const UNMANAGE_OUTCOME = 'UNMANAGE';

const PREVIEW_FAILED_DEPENDENCY_STATUS = 424;

const PREVIEW_FAILED_DEPENDENCY_MESSAGE =
    'Unable to preview conversion cleanliness for this pharmacy. Please try another pharmacy or contact support.';

const PREVIEW_FAILED_GENERIC_MESSAGE =
    'Unable to preview conversion cleanliness. Please try again.';

const CONVERT_MISSING_PREVIEW_ID_MESSAGE = 'Unable to initiate conversion. Please try again.';

const REROUTE_INELIGIBILITY_MESSAGE =
    'Conversion would cause referral to reroute to another pharmacy. To proceed with this conversion, please start a support conversation.';

const previewFailureMessage = (error?: { status?: number | string }) =>
    error?.status === PREVIEW_FAILED_DEPENDENCY_STATUS
        ? PREVIEW_FAILED_DEPENDENCY_MESSAGE
        : PREVIEW_FAILED_GENERIC_MESSAGE;

// Server owns the cleanliness rule; the dialog only gates on the verdict (+ never convert on UNMANAGE).
const canConvertProduct = ({
    wouldBeClean,
    wouldUnmanage,
}: {
    wouldBeClean?: boolean;
    wouldUnmanage?: boolean;
}) => !!wouldBeClean && !wouldUnmanage;

const isNextDisabledByProductChecks = ({
    isRunningPreview,
    previewFailed,
    wouldBeClean,
    wouldUnmanage,
    pharmacyNpi,
    conversionPreviewId,
}: {
    isRunningPreview: boolean;
    previewFailed: boolean;
    wouldBeClean?: boolean;
    wouldUnmanage?: boolean;
    pharmacyNpi?: string | null;
    conversionPreviewId?: string | null;
}) =>
    !!isRunningPreview ||
    !!previewFailed ||
    pharmacyNpi == null ||
    !conversionPreviewId ||
    !canConvertProduct({ wouldBeClean, wouldUnmanage });

const canShowRemainingFields = ({
    hasDestinationProduct,
    isRunningPreview,
    hasPreviewResponse,
    previewFailed,
}: {
    hasDestinationProduct: boolean;
    isRunningPreview: boolean;
    hasPreviewResponse: boolean;
    previewFailed: boolean;
}) => {
    if (!hasDestinationProduct || previewFailed) {
        return false;
    }
    return !isRunningPreview && hasPreviewResponse;
};

const claimsWereEvaluated = (previewData?: ConversionCleanlinessPreview) =>
    previewData?.payableProductServiceId != null ||
    previewData?.disposition != null ||
    previewData?.outcome != null ||
    previewData?.cleanReason != null ||
    previewData?.rejectCode != null;

const buildProductStatus = ({
    hasDestinationProduct,
    isRunningPreview,
    previewData,
    previewFailed,
    previewError,
}: {
    hasDestinationProduct: boolean;
    isRunningPreview: boolean;
    previewData?: ConversionCleanlinessPreview;
    previewFailed: boolean;
    previewError?: { status?: number | string };
}): ReactNode => {
    if (!hasDestinationProduct) {
        return undefined;
    }

    if (isRunningPreview) {
        return (
            <span className="sds_convert-biosimilar-dialog__product-status">
                <span className="sds_ellipsis-animation">Evaluating</span>
            </span>
        );
    }

    if (previewFailed) {
        return (
            <span className="sds_convert-biosimilar-dialog__product-status">
                <InlineMessage
                    intent={InlineMessage.Intent.ERROR}
                    label={previewFailureMessage(previewError)}
                />
            </span>
        );
    }

    // Wait until the preview answers before showing a dirty/clean status.
    if (previewData === undefined) {
        return undefined;
    }

    const { payableProductServiceId, hasActiveAuth, rerouteDuringRouteSim } = previewData;
    const canConvert = canConvertProduct({
        wouldBeClean: previewData.wouldBeClean,
        wouldUnmanage: !!previewData.wouldUnmanage || previewData.outcome === UNMANAGE_OUTCOME,
    });
    const evaluatedClaims = claimsWereEvaluated(previewData);

    const testClaimFailed = evaluatedClaims && !payableProductServiceId;
    let ineligibilityReason: string | null = null;
    if (rerouteDuringRouteSim) {
        ineligibilityReason = REROUTE_INELIGIBILITY_MESSAGE;
    } else if (!canConvert) {
        if (testClaimFailed && !hasActiveAuth) {
            ineligibilityReason = 'Test claim failed, no PA on file';
        } else if (testClaimFailed) {
            ineligibilityReason = 'Test claim failed';
        } else {
            ineligibilityReason = 'No PA on file';
        }
    } else if (!previewData.conversionPreviewId) {
        ineligibilityReason = PREVIEW_FAILED_GENERIC_MESSAGE;
    }

    if (!ineligibilityReason) {
        return (
            <span className="sds_convert-biosimilar-dialog__product-status">
                <InlineMessage
                    intent={InlineMessage.Intent.SUCCESS}
                    label="Product eligible for conversion"
                />
            </span>
        );
    }

    return (
        <span className="sds_convert-biosimilar-dialog__product-status">
            <InlineMessage
                intent={InlineMessage.Intent.ERROR}
                title="Product ineligible for conversion"
                label={ineligibilityReason}
                wrapText
            />
        </span>
    );
};

const buildReferralRouteCheckArgs = ({
    destinationProduct,
    destinationSupplierNpi,
    therapeuticCategory,
    referralDetails,
    memberDetails,
}: {
    destinationProduct: BiosimilarConversionProduct;
    destinationSupplierNpi: string;
    therapeuticCategory?: string;
    referralDetails: ReferralDetails;
    memberDetails: ConvertBiosimilarMemberDetails;
}) => ({
    segmentId: referralDetails.segmentId,
    memberId: memberDetails.memberId,
    patientFirstName: memberDetails.firstName,
    patientDob: memberDetails.dateOfBirth,
    productServiceOid: destinationProduct.value,
    isBrand: destinationProduct.is_brand,
    benefit: referralDetails.benefit,
    memberBenefitGroup: memberDetails.benefitGroup ?? null,
    patientZip: memberDetails.memberZip,
    supplierNpi: destinationSupplierNpi,
    prescriberNpi: referralDetails.prescriberNpi,
    placeOfService: referralDetails.placeOfService,
    therapeuticCategory,
    preferredPharmacyOffer: {
        parentPharmacyNpi: destinationSupplierNpi,
        routeCode: RouteCodes.ROFR,
        allowRouteOverride: true,
    },
});

const OfferRateDisplay = ({ offerRate = undefined }: { offerRate?: OfferRate }) => (
    <DetailLabelValuePair
        className="margin__bottom--medium"
        label="Offer Rate"
        value={
            <ServicingRate
                servicingRate={offerRate?.rate}
                referentPriceType={offerRate?.referentPriceType}
            />
        }
    />
);

const SelectProductStep = ({
    biosimilarConversionOptions = undefined,
    isLoadingOptions,
    isRunningPreview,
    previewData = undefined,
    previewFailed,
    previewError = undefined,
    offerRate = undefined,
    onDestinationProductChange,
}: {
    biosimilarConversionOptions?: BiosimilarConversionProduct[];
    isLoadingOptions: boolean;
    isRunningPreview: boolean;
    previewData?: ConversionCleanlinessPreview;
    previewFailed: boolean;
    previewError?: { status?: number | string };
    offerRate?: OfferRate;
    onDestinationProductChange: (product?: BiosimilarConversionProduct) => void;
}) => {
    const { values, setFieldValue, validateForm } = useFormikContext<ConvertBiosimilarFormValues>();

    // MultiStepDialog does not validate on mount; re-validate when preview state changes so Next stays gated.
    useEffect(() => {
        void validateForm();
    }, [validateForm, isRunningPreview, previewFailed, previewData]);

    return (
        <>
            <SuggestSingleSelectFormik
                id="destinationProduct"
                label="Product"
                required
                disableAutoPopulate
                values={biosimilarConversionOptions}
                isLoading={isLoadingOptions}
                selectedItem={values.destinationProduct}
                itemsEqual={(a?: BiosimilarConversionProduct, b?: BiosimilarConversionProduct) =>
                    a?.value === b?.value && a?.is_brand === b?.is_brand
                }
                onChange={(product?: BiosimilarConversionProduct) => {
                    void (async () => {
                        await setFieldValue('destinationProduct', product);
                        await setFieldValue('acceptRate', []);
                        onDestinationProductChange(product);
                    })();
                }}
                helperText={buildProductStatus({
                    hasDestinationProduct: !!values.destinationProduct,
                    isRunningPreview,
                    previewData,
                    previewFailed,
                    previewError,
                })}
            />
            {canShowRemainingFields({
                hasDestinationProduct: !!values.destinationProduct,
                isRunningPreview,
                hasPreviewResponse: previewData !== undefined,
                previewFailed,
            }) &&
                previewData?.supplierNpi != null &&
                !!previewData.conversionPreviewId &&
                canConvertProduct({
                    wouldBeClean: previewData.wouldBeClean,
                    wouldUnmanage:
                        !!previewData.wouldUnmanage || previewData.outcome === UNMANAGE_OUTCOME,
                }) && (
                    <>
                        <OfferRateDisplay offerRate={offerRate} />
                        <ReasonInput reasons={ReasonOptions} hideSource />
                    </>
                )}
        </>
    );
};

const acceptRateValidationSchema = Yup.object({
    acceptRate: Yup.array()
        .of(Yup.string())
        .ensure()
        .min(2, validationStrings.requiredField.errorMessage)
        .test(
            'both-agreements',
            validationStrings.requiredField.errorMessage,
            (value) =>
                !!value?.includes(AcceptRateOptions.AGREE_TO_RATE) &&
                !!value?.includes(AcceptRateOptions.AGREE_TO_TERMS),
        ),
});

interface SourceProduct {
    description: string;
    brandDescription: string;
    genericDescription: string;
}

const AcceptRateStep = ({
    memberDetails = undefined,
    offerRate = undefined,
    sourceProduct = undefined,
}: {
    memberDetails?: ConvertBiosimilarMemberDetails;
    offerRate?: OfferRate;
    sourceProduct?: SourceProduct;
}) => {
    const { values, validateForm } = useFormikContext<ConvertBiosimilarFormValues>();
    const { destinationProduct } = values;

    // MultiStepDialog does not validate on mount; ensure Accept Rate starts disabled.
    useEffect(() => {
        void validateForm();
    }, [validateForm]);

    if (!destinationProduct || !offerRate) {
        return null;
    }

    return (
        <>
            <div className="sds_convert-biosimilar-dialog__rate-conversion-message padding__bottom--medium">{`${sourceProduct?.description} for ${formatNameLastFirst(memberDetails?.lastName, memberDetails?.firstName)} will be converted to`}</div>
            <div className="sds_convert-biosimilar-dialog__rate-container padding__top--medium padding__bottom--medium">
                <div className="sds_convert-biosimilar-dialog__rate-value">
                    {`${destinationProduct.name} at ${formatRateString({
                        bidDecimal: offerRate.rate,
                        referentPriceType: offerRate.referentPriceType,
                    })}`}
                </div>
            </div>
            <div className="sds_dialog__section-text padding__top--medium">Accept</div>
            <CheckboxListFormik
                name="acceptRate"
                required
                options={[
                    {
                        value: AcceptRateOptions.AGREE_TO_RATE,
                        component: (
                            <span
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                }}
                            >
                                <span className="margin__right--x-small">I agree to</span>
                                <ServicingRate
                                    servicingRate={offerRate.rate}
                                    referentPriceType={offerRate.referentPriceType}
                                />
                            </span>
                        ),
                    },
                    {
                        value: AcceptRateOptions.AGREE_TO_TERMS,
                        component: (
                            <span>
                                <span>{'I agree to the '}</span>
                                <Hyperlink
                                    linkText="Terms & Conditions"
                                    id="convert-biosimilar-terms-and-conditions-link"
                                    navUrl={AppRoutes.termsAndConditions}
                                />
                                <span>.</span>
                            </span>
                        ),
                    },
                ]}
            />
            <InlineMessage
                intent={InlineMessage.Intent.WARNING}
                label="The current referral will be expired and a new one will be created."
                wrapText
            />
        </>
    );
};

const buildSelectProductValidationSchema = ({
    isRunningPreview,
    previewFailed,
    wouldBeClean,
    wouldUnmanage,
    pharmacyNpi,
    conversionPreviewId,
}: {
    isRunningPreview: boolean;
    previewFailed: boolean;
    wouldBeClean?: boolean;
    wouldUnmanage: boolean;
    pharmacyNpi?: string | null;
    conversionPreviewId?: string | null;
}) =>
    Yup.object({
        // mixed (not object) so a missing selection stays invalid — Yup.object() coerces
        // undefined to {}, which would enable Next before a product is chosen.
        destinationProduct: Yup.mixed<BiosimilarConversionProduct>()
            .required(validationStrings.requiredField.errorMessage)
            .test(
                'has-product-value',
                validationStrings.requiredField.errorMessage,
                (product) => !!product?.value,
            ),
        // Silent Next gate for preview/cleanliness — not bound to a field, so no inline error.
        canAdvanceFromPreview: Yup.mixed().test(
            'convertible-product',
            ' ',
            () =>
                !isNextDisabledByProductChecks({
                    isRunningPreview,
                    previewFailed,
                    wouldBeClean,
                    wouldUnmanage,
                    pharmacyNpi,
                    conversionPreviewId,
                }),
        ),
        ...ReasonInput.validationSchema,
    });

const submitBiosimilarConversion = async ({
    referralDetails,
    values,
    convertBiosimilar,
    worklistTask,
    postConvert,
    dispatch,
    convertedRef,
    conversionPreviewId,
    onSuccess,
}: {
    referralDetails?: ReferralDetails;
    values: ConvertBiosimilarFormValues;
    convertBiosimilar: ReturnType<typeof useConvertBiosimilarMutation>[0];
    worklistTask?: WorklistTask;
    postConvert: ReturnType<typeof usePostConvertMutation>[0];
    dispatch: ReturnType<typeof useAppDispatch>;
    convertedRef: { current: boolean };
    conversionPreviewId: string;
    onSuccess: () => void;
}) => {
    if (!referralDetails) {
        return;
    }
    const { payerId, treatmentId } = referralDetails;
    try {
        if (!convertedRef.current) {
            await convertBiosimilar({
                payerId,
                treatmentId,
                body: {
                    destination_product_service_oid: values.destinationProduct!.value,
                    destination_is_brand: values.destinationProduct!.is_brand,
                    conversion_preview_id: conversionPreviewId,
                    ...ReasonInput.submitValues(values),
                },
            }).unwrap();
            convertedRef.current = true;
        }
        if (worklistTask) {
            await postConvert({ taskId: worklistTask.task_id }).unwrap();
            await dispatch(fetchWorklistTasks());
        }
        SuccessToast({
            message: 'Biosimilar conversion initiated successfully.',
        });
        onSuccess();
    } catch (err) {
        if (!convertedRef.current) {
            handleError(getConvertBiosimilarErrorMessage(err as { status?: number }), err as Error);
        }
    }
};

const getBiosimilarConversionProductsArgs = (
    referralDetails?: ReferralDetails,
    memberDetails?: ConvertBiosimilarMemberDetails,
) => {
    const conversionMemberState = memberDetails?.memberState ?? referralDetails?.memberState;
    if (!referralDetails?.productServiceOid || !conversionMemberState) {
        return skipToken;
    }
    return {
        productServiceOid: referralDetails.productServiceOid,
        payerId: referralDetails.payerId,
        segmentId: referralDetails.segmentId,
        memberState: conversionMemberState,
        benefit: referralDetails.benefit,
        placeOfService: referralDetails.placeOfService,
        lineOfBusiness: memberDetails?.lineOfBusiness,
        lobSegment: memberDetails?.lobSegment,
    };
};

const useConvertBiosimilarDialogState = ({
    referralDetails,
    memberDetails,
    onClose,
}: {
    referralDetails?: ReferralDetails;
    memberDetails?: ConvertBiosimilarMemberDetails;
    onClose: () => void;
}) => {
    // The referral stays with the pharmacy already servicing it, so it is the only destination.
    const currentSupplierNpi = referralDetails?.supplierNpi;
    const { data: biosimilarConversionOptions, isFetching: biosimilarConversionOptionsIsFetching } =
        useGetBiosimilarConversionProductsQuery(
            getBiosimilarConversionProductsArgs(referralDetails, memberDetails),
        );
    const { data: configuredProducts, isFetching: configuredProductsIsFetching } =
        useGetConfiguredProductsForPayerQuery(
            referralDetails?.segmentId ? { segmentId: referralDetails.segmentId } : skipToken,
        );
    const [convertBiosimilar] = useConvertBiosimilarMutation();
    const [
        runConversionCleanlinessPreview,
        {
            isLoading: isRunningPreview,
            data: previewData,
            error: previewError,
            isError: previewFailed,
            reset: resetConversionCleanlinessPreview,
        },
    ] = useRunConversionCleanlinessPreviewMutation();

    const offerRate = useMemo(() => {
        const rate = previewData?.offerRate;
        if (rate === undefined || rate === null) {
            return undefined;
        }

        return { rate, referentPriceType: previewData?.referentPriceType ?? undefined };
    }, [previewData]);

    const wouldUnmanage = !!previewData?.wouldUnmanage || previewData?.outcome === UNMANAGE_OUTCOME;

    const selectProductValidationSchema = useMemo(
        () =>
            buildSelectProductValidationSchema({
                isRunningPreview,
                previewFailed,
                wouldBeClean: previewData?.wouldBeClean,
                wouldUnmanage,
                pharmacyNpi: previewData?.supplierNpi,
                conversionPreviewId: previewData?.conversionPreviewId,
            }),
        [
            isRunningPreview,
            previewFailed,
            previewData?.wouldBeClean,
            wouldUnmanage,
            previewData?.supplierNpi,
            previewData?.conversionPreviewId,
        ],
    );

    const handleClose = () => {
        resetConversionCleanlinessPreview();
        onClose();
    };

    const runConversionCleanliness = async (
        product: BiosimilarConversionProduct,
        parentSupplierNpi: string,
    ) => {
        if (!referralDetails || !memberDetails) {
            return;
        }

        const therapeuticCategory = configuredProducts?.find(
            ({ oid }) => oid === product.value,
        )?.therapeuticCategory;

        try {
            await runConversionCleanlinessPreview({
                payerId: referralDetails.payerId,
                treatmentId: referralDetails.treatmentId,
                referralId: referralDetails.referralId,
                segmentId: referralDetails.segmentId,
                memberId: memberDetails.memberId,
                patientFirstName: memberDetails.firstName,
                patientLastName: memberDetails.lastName,
                patientDob: memberDetails.dateOfBirth,
                prescriberNpi: referralDetails.prescriberNpi,
                productServiceIds: product.productServiceIds ?? [],
                productServiceOid: product.value,
                asOfDate: formatDate(dayjs(), DateFormats.HYPHEN_DATE),
                parentSupplierNpi,
                lineOfBusiness: memberDetails.lineOfBusiness,
                lobSegment: memberDetails.lobSegment,
                memberState: memberDetails.memberState ?? referralDetails.memberState,
                // marketplace_participants are filled server-side on the common cleanliness path.
                routeCheck: buildReferralRouteCheckArgs({
                    destinationProduct: product,
                    destinationSupplierNpi: parentSupplierNpi,
                    therapeuticCategory,
                    referralDetails,
                    memberDetails,
                }),
            }).unwrap();
        } catch (err) {
            handleError(previewFailureMessage(err as { status?: number }), err as Error);
        }
    };

    const handleDestinationProductChange = (product?: BiosimilarConversionProduct) => {
        resetConversionCleanlinessPreview();

        if (!product || !currentSupplierNpi || !referralDetails || !memberDetails) {
            return;
        }

        void runConversionCleanliness(product, currentSupplierNpi);
    };

    return {
        biosimilarConversionOptions,
        isLoadingOptions: biosimilarConversionOptionsIsFetching || configuredProductsIsFetching,
        isRunningPreview,
        previewData,
        previewFailed,
        previewError: previewError && 'status' in previewError ? previewError : undefined,
        offerRate,
        selectProductValidationSchema,
        handleClose,
        handleDestinationProductChange,
        convertBiosimilar,
        referralDetails,
    };
};

interface ConvertBiosimilarDialogProps {
    isOpen?: boolean;
    onClose: () => void;
    referralDetails?: ReferralDetails;
    memberDetails?: ConvertBiosimilarMemberDetails;
    sourceProduct?: SourceProduct;
    worklistTask?: WorklistTask;
}

export const ConvertBiosimilarDialog = ({
    isOpen = false,
    onClose,
    referralDetails = undefined,
    memberDetails = undefined,
    sourceProduct = undefined,
    worklistTask = undefined,
}: ConvertBiosimilarDialogProps) => {
    const dispatch = useAppDispatch();
    const [postConvert] = usePostConvertMutation();
    // Once conversion succeeds, another Accept Rate only retries marking the worklist task converted.
    const convertedRef = useRef(false);
    const {
        biosimilarConversionOptions,
        isLoadingOptions,
        isRunningPreview,
        previewData,
        previewFailed,
        previewError,
        offerRate,
        selectProductValidationSchema,
        handleClose: closeDialog,
        handleDestinationProductChange,
        convertBiosimilar,
    } = useConvertBiosimilarDialogState({ referralDetails, memberDetails, onClose });

    const handleClose = () => {
        convertedRef.current = false;
        closeDialog();
    };

    return (
        <MultiStepDialog
            dialogTitle={Title}
            primaryButtonLabel="Accept Rate"
            secondaryButtonLabel="Cancel"
            isOpen={isOpen}
            width={560}
            onSecondaryButtonClick={handleClose}
            stepComponents={[
                {
                    label: 'Select Product',
                    component: (
                        <SelectProductStep
                            biosimilarConversionOptions={biosimilarConversionOptions}
                            isLoadingOptions={isLoadingOptions}
                            isRunningPreview={isRunningPreview}
                            previewData={previewData}
                            previewFailed={previewFailed}
                            previewError={previewError}
                            offerRate={offerRate}
                            onDestinationProductChange={handleDestinationProductChange}
                        />
                    ),
                    validationSchema: selectProductValidationSchema,
                },
                {
                    label: 'Accept Rate',
                    component: (
                        <AcceptRateStep
                            memberDetails={memberDetails}
                            offerRate={offerRate}
                            sourceProduct={sourceProduct}
                        />
                    ),
                    validationSchema: acceptRateValidationSchema,
                },
            ]}
            onSubmit={async (values, { resetForm }) => {
                if (!previewData?.conversionPreviewId) {
                    handleError(CONVERT_MISSING_PREVIEW_ID_MESSAGE);
                    return;
                }
                await submitBiosimilarConversion({
                    referralDetails,
                    values: values as ConvertBiosimilarFormValues,
                    convertBiosimilar,
                    worklistTask,
                    postConvert,
                    dispatch,
                    convertedRef,
                    conversionPreviewId: previewData.conversionPreviewId,
                    onSuccess: () => {
                        resetForm();
                        handleClose();
                    },
                });
            }}
        />
    );
};

ConvertBiosimilarDialog.Title = Title;
